/**
 * The two theme views' bases, phrased in the reader's language.
 *
 * A basis is a *claim about the arithmetic* — "exposure counts a name in every
 * theme it holds, so this column does not sum to 100%". The server owns which
 * claim applies (`basis_*_code`) and states it in prose (`basis_*`); the client
 * only chooses the words. Neither half is sufficient alone:
 *
 * - prose alone cannot be translated without the client matching English strings,
 *   which is how a basis silently drifts from the endpoint that owns it;
 * - a code alone goes blank on a client that has not learned a code the server
 *   started sending, which is worse than English.
 *
 * So both travel together and an unknown code falls back to the server's sentence.
 * A translated basis is a convenience; a *missing* basis is a defect, and this
 * helper cannot produce one.
 */

import type { TFunction } from 'i18next';

/** Server basis code -> locale key. The only place the pairing is written down. */
export const BASIS_KEYS: Record<string, string> = {
  exposure_live_tags: 'dashboard.basisExposureLiveTags',
  primary_reviewed: 'dashboard.basisPrimaryReviewed',
};

/**
 * Localised basis for `code`, or `fallback` (the server's own sentence) when the
 * code is missing or unknown.
 */
export function basisText(t: TFunction, code: string | null | undefined, fallback: string): string {
  const key = code ? BASIS_KEYS[code] : undefined;
  if (!key) return fallback;
  return t(key, { defaultValue: fallback });
}
